import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { showToast } from "../../utils/customToast";
import { useCompany } from "../../context/CompanyContext";
import { getFinancialYear } from "../../utils/financialYearHelper";
import {
  FaSlidersH,
  FaFileInvoice,
  FaBook,
  FaSave,
  FaUndo,
  FaCheckCircle,
  FaCalendarAlt,
} from "react-icons/fa";
import "./Configuration.css";
import { ConfigurationSkeleton } from "../Skeleton";

const BASE_API_URL =
  (process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api").replace(
    /\/api\/?$/,
    ""
  ) + "/api";

export default function Configuration() {
  const { selectedCompany } = useCompany();

  // Active Company ID
  const companyId = useMemo(() => {
    if (selectedCompany?.id || selectedCompany?.CompID) {
      return selectedCompany.id || selectedCompany.CompID;
    }
    const saved = localStorage.getItem("selectedCompany");
    if (saved) {
      try {
        const obj = JSON.parse(saved);
        return obj.id || obj.CompID || 1147;
      } catch (e) {}
    }
    return localStorage.getItem("companyId") || 1147;
  }, [selectedCompany]);

  // Form State
  const [formData, setFormData] = useState({
    prefix: "",
    suffix: "",
    resetInvoiceNo: true,
    invoiceStartNo: 1,
    incomeHead: "",
    expenseHead: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headsData, setHeadsData] = useState({
    all: [],
    incomeHeads: [],
    expenseHeads: [],
  });

  // Current financial year details
  const currentFY = useMemo(() => getFinancialYear(new Date()), []);

  // Fetch configuration on company change
  const fetchConfig = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/configuration/${companyId}`);
      if (res.data?.success) {
        const config = res.data.data;
        setFormData({
          prefix: config.prefix || "",
          suffix: config.suffix || "",
          resetInvoiceNo: config.resetInvoiceNo ?? true,
          invoiceStartNo: config.invoiceStartNo || 1,
          incomeHead: config.incomeHead || "",
          expenseHead: config.expenseHead || "",
        });
        if (res.data.heads) {
          setHeadsData(res.data.heads);
        }
      }
    } catch (err) {
      console.error("Failed to load configuration:", err);
      showToast.error("Failed to load company configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [companyId]);

  // Live Invoice Number Preview Calculation (No predefined slashes; user-defined)
  const previewInvoiceNo = useMemo(() => {
    const pfx = (formData.prefix || "").trim();
    const sfx = (formData.suffix || "").trim();
    const num = "1";
    return `${pfx}${num}${sfx}`;
  }, [formData.prefix, formData.suffix]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Submit Configuration
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!companyId) {
      showToast.error("No company selected");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        prefix: formData.prefix.trim(),
        suffix: formData.suffix.trim(),
        resetInvoiceNo: Boolean(formData.resetInvoiceNo),
        invoiceStartNo: parseInt(formData.invoiceStartNo, 10) || 1,
        incomeHead: formData.incomeHead.trim(),
        expenseHead: formData.expenseHead.trim(),
      };

      const res = await axios.post(`${BASE_API_URL}/configuration/${companyId}`, payload);
      if (res.data?.success) {
        showToast.success("Configuration saved successfully!");
        await fetchConfig(); // Refresh heads list in case new ledgers were created
      } else {
        showToast.error(res.data?.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Save Configuration Error:", err);
      showToast.error("Error saving configuration: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Reset to Defaults
  const handleResetToDefaults = () => {
    setFormData({
      prefix: "DDT/",
      suffix: `/${currentFY}`,
      resetInvoiceNo: true,
      invoiceStartNo: 1,
      incomeHead: "",
      expenseHead: "",
    });
    showToast.info("Reset to default configuration values");
  };

  return (
    <div className="config-page-wrapper">
      {/* Top Header */}
      <div className="config-header-row">
        <div className="config-header-left">
          <div className="config-breadcrumb">
            <span>Dashboard</span> / <span>Utility</span> / <b>Configuration</b>
          </div>
          <h1 className="config-title">
            <FaSlidersH className="config-title-icon" />
            System &amp; Accounting Configuration
          </h1>
          <p className="config-subtitle">
            Configure invoice numbering formats, automated financial year reset rules, and default accounting ledger heads.
          </p>
        </div>
      </div>

      {loading ? (
        <ConfigurationSkeleton />
      ) : (
        <form onSubmit={handleSave} className="config-form-container">
          {/* ========================================================
              CARD 1: INVOICE NUMBERING CONFIGURATION (COMPACT ONE-ROW)
             ======================================================== */}
          <div className="config-card">
            <div className="config-card-header config-card-header-3col">
              <div className="config-card-title-group">
                <div className="config-card-icon-wrap teal-icon">
                  <FaFileInvoice />
                </div>
                <div>
                  <h2 className="config-card-title">Invoice Numbering Configuration</h2>
                  <p className="config-card-desc">
                    Control invoice prefix, suffix, and yearly number reset.
                  </p>
                </div>
              </div>

              {/* Middle: Preview Badge */}
              <div className="config-header-center">
                <div className="config-preview-badge">
                  <FaCheckCircle className="preview-check-icon" />
                  <span>Preview:</span>
                  <span className="preview-code-tag">{previewInvoiceNo}</span>
                </div>
              </div>

              {/* Right: Active FY Badge */}
              <div className="config-badge-fy">
                <FaCalendarAlt /> Active FY: <b>{currentFY}</b>
              </div>
            </div>

            <div className="config-card-body">
              {/* Prefix, Suffix, and Reset in ONE ROW */}
              <div className="config-one-row-grid">
                {/* Invoice Prefix */}
                <div className="config-field-compact">
                  <label htmlFor="inv-prefix" className="config-label-compact">
                    Invoice No Prefix
                  </label>
                  <input
                    id="inv-prefix"
                    type="text"
                    className="config-input-compact"
                    placeholder="e.g. RIW- or INV/"
                    value={formData.prefix}
                    maxLength={20}
                    onChange={(e) => handleChange("prefix", e.target.value)}
                  />
                  <span className="config-micro-hint">Max 20 chars</span>
                </div>

                {/* Invoice Suffix */}
                <div className="config-field-compact">
                  <label htmlFor="inv-suffix" className="config-label-compact">
                    Invoice No Suffix
                  </label>
                  <input
                    id="inv-suffix"
                    type="text"
                    className="config-input-compact"
                    placeholder="e.g. -2026-27 or /2026-2027"
                    value={formData.suffix}
                    maxLength={20}
                    onChange={(e) => handleChange("suffix", e.target.value)}
                  />
                  <span className="config-micro-hint">Max 20 chars</span>
                </div>

                {/* Financial Year Reset */}
                <div className="config-field-compact reset-box-wrapper">
                  <label className="config-label-compact">
                    Financial Year Reset Rule
                  </label>
                  <label className="config-checkbox-inline" htmlFor="reset-fy-checkbox">
                    <input
                      id="reset-fy-checkbox"
                      type="checkbox"
                      className="config-checkbox-native"
                      checked={formData.resetInvoiceNo}
                      onChange={(e) => handleChange("resetInvoiceNo", e.target.checked)}
                    />
                    <span className="checkbox-text-inline">
                      Reset to 1 on FY Change (01-Apr)
                    </span>
                    <span className={`reset-status-pill ${formData.resetInvoiceNo ? "active" : "inactive"}`}>
                      {formData.resetInvoiceNo ? "Enabled" : "Off"}
                    </span>
                  </label>
                  <span className="config-micro-hint">Starts from 1 every April 1st</span>
                </div>
              </div>
            </div>
          </div>

          {/* ========================================================
              CARD 2: ACCOUNTING DEFAULT HEADS (NO DROPDOWN, COMPACT)
             ======================================================== */}
          <div className="config-card">
            <div className="config-card-header">
              <div className="config-card-title-group">
                <div className="config-card-icon-wrap teal-icon">
                  <FaBook />
                </div>
                <div>
                  <h2 className="config-card-title">Accounting Default Heads</h2>
                  <p className="config-card-desc">
                    Specify default ledger names for automated Sales &amp; Journal voucher posting.
                  </p>
                </div>
              </div>
            </div>

            <div className="config-card-body">
              <div className="config-two-col-grid">
                {/* Default Income Head */}
                <div className="config-field-compact">
                  <label htmlFor="income-head-input" className="config-label-compact">
                    Default Income Head
                  </label>
                  <input
                    id="income-head-input"
                    type="text"
                    className="config-input-compact"
                    list="income-heads-datalist"
                    placeholder="Type or select Income Ledger (e.g. TOUR INCOME AC)"
                    value={formData.incomeHead}
                    onChange={(e) => handleChange("incomeHead", e.target.value)}
                  />
                  <datalist id="income-heads-datalist">
                    {headsData.incomeHeads.map((h) => (
                      <option key={`inc-${h.HEAD_CODE}`} value={h.HEADName} />
                    ))}
                  </datalist>
                </div>

                {/* Default Expense Head */}
                <div className="config-field-compact">
                  <label htmlFor="expense-head-input" className="config-label-compact">
                    Default Expense Head
                  </label>
                  <input
                    id="expense-head-input"
                    type="text"
                    className="config-input-compact"
                    list="expense-heads-datalist"
                    placeholder="Type or select Expense Ledger (e.g. TOUR EXP EACCOUNT)"
                    value={formData.expenseHead}
                    onChange={(e) => handleChange("expenseHead", e.target.value)}
                  />
                  <datalist id="expense-heads-datalist">
                    {headsData.expenseHeads.map((h) => (
                      <option key={`exp-${h.HEAD_CODE}`} value={h.HEADName} />
                    ))}
                  </datalist>
                </div>
              </div>
            </div>
          </div>

          {/* ========================================================
              ACTION BUTTONS BAR
             ======================================================== */}
          <div className="config-actions-bar">
            <button
              type="button"
              className="config-btn config-btn-outline"
              onClick={handleResetToDefaults}
              disabled={saving}
            >
              <FaUndo /> Reset Defaults
            </button>

            <button
              type="submit"
              className="config-btn config-btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="config-btn-spinner"></span> Saving...
                </>
              ) : (
                <>
                  <FaSave /> Save Configuration
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
